import type { BodyUUID, LightUUID, SceneLight, SiteUUID, Vector3 } from "../../types/scene";
import { create } from "mutative";
import { generateUuid } from "../../utils/id";
import type { SceneStoreGetState, SceneStoreSetState } from "../sceneStore.types";
import type { LightActions, SceneLightInput } from "./actionTypes";
import { withHistory } from "./withHistory";
import { shouldBlockSceneAuthoringMutation } from "../../utils/physicsGuards";
import { namesFromRecord, nextUniqueName } from "../../utils/uniqueNames";
import { logger } from "../../utils/logger";

const clamp = (v: number, lo: number, hi: number): number => Math.max(lo, Math.min(hi, v));

const toVec3 = (v: unknown, fallback: Vector3): Vector3 => {
  if (!Array.isArray(v) || v.length < 3) return [...fallback];
  const x = Number(v[0]);
  const y = Number(v[1]);
  const z = Number(v[2]);
  return [
    Number.isFinite(x) ? x : fallback[0],
    Number.isFinite(y) ? y : fallback[1],
    Number.isFinite(z) ? z : fallback[2],
  ];
};

const normalizeDirection = (v: unknown): Vector3 => {
  const raw = toVec3(v, [0, 0, -1]);
  const len = Math.hypot(raw[0], raw[1], raw[2]);
  if (len < 1e-8) return [0, 0, -1];
  return [raw[0] / len, raw[1] / len, raw[2] / len];
};

const asBool = (value: unknown, fallback: boolean): boolean => {
  if (typeof value === "boolean") return value;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (normalized === "true" || normalized === "1") return true;
    if (normalized === "false" || normalized === "0") return false;
  }
  if (typeof value === "number" && Number.isFinite(value)) return value !== 0;
  return fallback;
};

const asBrandedUuid = <T extends string>(value: unknown): T | undefined => {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed ? (trimmed as T) : undefined;
};

const normalizeLight = (light: SceneLightInput): SceneLight => {
  const uuid = (light.uuid || generateUuid()) as LightUUID;
  const kind = String(light.kind || "fixed") === "mounted" ? "mounted" : "fixed";
  const mountType = String(light.mountType || "body") === "site" ? "site" : "body";
  const type = String(light.type || "spot").trim() || "spot";
  return {
    uuid,
    name: String(light.name || `light_${uuid}`),
    kind,
    mountType: kind === "mounted" ? mountType : undefined,
    mountBodyUuid:
      kind === "mounted" && mountType === "body" && light.mountBodyUuid
        ? asBrandedUuid<BodyUUID>(light.mountBodyUuid)
        : undefined,
    mountSiteUuid:
      kind === "mounted" && mountType === "site" && light.mountSiteUuid
        ? asBrandedUuid<SiteUUID>(light.mountSiteUuid)
        : undefined,
    position: toVec3(light.position, [0, 0, 2]),
    direction: normalizeDirection(light.direction),
    type,
    active: asBool(light.active, true),
    castshadow: asBool(light.castshadow, true),
    diffuse: toVec3(light.diffuse, [0.7, 0.7, 0.7]),
    ambient: toVec3(light.ambient, [0, 0, 0]),
    specular: toVec3(light.specular, [0.3, 0.3, 0.3]),
    range: Math.max(0, Number(light.range) || 10),
    cutoff: clamp(Number(light.cutoff) || 45, 0, 180),
    exponent: Math.max(0, Number(light.exponent) || 10),
    attenuation: toVec3(light.attenuation, [1, 0, 0]),
    intensity: Number.isFinite(Number(light.intensity)) ? Number(light.intensity) : undefined,
    bulbradius:
      Number.isFinite(Number(light.bulbradius)) && Number(light.bulbradius) >= 0
        ? Number(light.bulbradius)
        : undefined,
    trackingMode: typeof light.trackingMode === "string" ? light.trackingMode : undefined,
    targetBodyUuid: light.targetBodyUuid ? asBrandedUuid<BodyUUID>(light.targetBodyUuid) : undefined,
    targetBodyName: typeof light.targetBodyName === "string" ? light.targetBodyName : undefined,
    attrs: light.attrs && typeof light.attrs === "object" ? { ...light.attrs } : undefined,
  };
};

const resolveLightMount = (
  light: SceneLight,
  bodies: Record<string, unknown> | undefined,
  sites: Record<string, unknown> | undefined
): SceneLight => {
  if (light.kind !== "mounted") return light;
  if (light.mountType === "site") {
    const siteUuid = light.mountSiteUuid;
    if (!siteUuid || !sites?.[siteUuid]) {
      logger.warn(
        `[lightActions] Light "${light.name}" targets missing site "${siteUuid ?? "<unset>"}"; downgrading to fixed-world light.`
      );
      return {
        ...light,
        kind: "fixed",
        mountType: undefined,
        mountBodyUuid: undefined,
        mountSiteUuid: undefined,
      };
    }
  } else {
    const bodyUuid = light.mountBodyUuid;
    if (!bodyUuid || !bodies?.[bodyUuid]) {
      logger.warn(
        `[lightActions] Light "${light.name}" targets missing body "${bodyUuid ?? "<unset>"}"; downgrading to fixed-world light.`
      );
      return {
        ...light,
        kind: "fixed",
        mountType: undefined,
        mountBodyUuid: undefined,
        mountSiteUuid: undefined,
      };
    }
  }
  return light;
};

export const createLightActions = (
  set: SceneStoreSetState,
  get: SceneStoreGetState
): LightActions => ({
  setLights: (lightsInput: SceneLightInput[] | Record<string, SceneLightInput>) => {
    if (shouldBlockSceneAuthoringMutation("setLights")) return;
    const arr: SceneLightInput[] = Array.isArray(lightsInput)
      ? lightsInput
      : Object.values(lightsInput || {});
    const state = get();
    const next: Record<string, SceneLight> = {};
    arr.forEach((light) => {
      if (!light) return;
      const normalized = resolveLightMount(normalizeLight(light), state?.bodies, state?.sites);
      next[normalized.uuid] = normalized;
    });
    set((state) =>
      create(state, (draft) => {
        draft.lights = next;
      })
    );
  },

  addLight: withHistory((lightInput: SceneLightInput): LightUUID => {
    const state = get();
    const normalizedRaw = resolveLightMount(normalizeLight(lightInput), state?.bodies, state?.sites);
    const normalized = {
      ...normalizedRaw,
      name: nextUniqueName(normalizedRaw.name, namesFromRecord(state?.lights)),
    };
    set((state) =>
      create(state, (draft) => {
        draft.lights = draft.lights || {};
        draft.lights[normalized.uuid] = normalized;
      })
    );
    return normalized.uuid;
  }),

  updateLight: withHistory((lightUuid: LightUUID, updates: Partial<SceneLightInput>) => {
    const state = get();
    set((state) =>
      create(state, (draft) => {
        const prev = draft.lights?.[lightUuid];
        if (!prev) return;
        draft.lights[lightUuid] = resolveLightMount(
          normalizeLight({ ...prev, ...updates, uuid: lightUuid }),
          state?.bodies,
          state?.sites
        );
      })
    );
  }),

  removeLight: withHistory((lightUuid: LightUUID) => {
    set((state) =>
      create(state, (draft) => {
        if (!draft.lights) return;
        delete draft.lights[lightUuid];
      })
    );
  }),
});
